// `aod-map` v1 — the authored-map document (docs/aod-studio-design.md §3.1).
//
// AOD Studio edits this directly; the sim loads it through `setup_from_map`
// and validates it again in Rust. It is NOT a save: it is the thing an author
// edits. The document says WHERE (tiles, rivers, starts); the ruleset says
// HOW. `victory` is absent = the preset's defaults, so a custom map never
// forces a victory change — it composes with the preset choice.
//
// The biome vocabulary is AoD's nine `BiomeKind`s, first-party here (the
// owner owns both ends). Module ids in `ruleset.modules` must be a subset of
// MODULE_IDS; a map never *arms* a module the preset did not choose — the
// only module a map implies is the dark `map.custom`, which setup arms
// itself.

import type { Ruleset, Seat } from './ruleset.js';

export const AOD_MAP_SCHEMA_VERSION = 'aod-map/1';

/** AoD's nine tile biomes — mirrors Rust `BiomeKind::as_str` exactly. */
export const AOD_BIOMES = [
  'plains',
  'forest',
  'hills',
  'mountain',
  'desert',
  'tundra',
  'coast',
  'ocean',
  'river',
] as const;

export type AodMapBiome = (typeof AOD_BIOMES)[number];

/** Open, advisory enum in v1 — stored, nothing branches on it yet. */
export const AOD_PLACE_KINDS = ['city', 'landmark', 'ruin', 'harbour'] as const;
export type AodPlaceKind = (typeof AOD_PLACE_KINDS)[number];

export interface AodMapTile {
  readonly q: number;
  readonly r: number;
  readonly biome: AodMapBiome;
  /** 0..255 render perturbation; absent = 0. */
  readonly variant?: number;
  /** 0..=12; only meaningful when `terrain.levels` is armed. */
  readonly terrainLevel?: number;
}

export interface AodMapRiver {
  readonly q: number;
  readonly r: number;
  /** Edge bitmask, bit i = the edge toward AXIAL_DIRS[i]; symmetric. */
  readonly edges: number;
  readonly bridges: number;
}

export interface AodMapWall {
  /** Ring index from the board edge: 0 = the outermost ring (CWall.row). */
  readonly row: number;
}

export interface AodMapResource {
  readonly q: number;
  readonly r: number;
  readonly kind: string;
}

export interface AodMapCity {
  readonly q: number;
  readonly r: number;
  readonly name: string;
  readonly tier: number;
}

export interface AodMapStart {
  readonly playerIndex: number;
  readonly q: number;
  readonly r: number;
}

export interface AodMapPlace {
  /** Stable slug — a mission refers to a place by id ("the royal held at Sunspire"). */
  readonly id: string;
  readonly name: string;
  readonly q: number;
  readonly r: number;
  readonly kind?: AodPlaceKind;
}

/**
 * Optional victory overrides — mirror `VictoryConfig` fields one-to-one,
 * integers only, validator-bounded. Absent = the preset's defaults.
 */
export interface AodMapVictory {
  readonly paths?: readonly string[];
  readonly diplomacyHonor?: number;
  readonly minEmpireTick?: number;
  readonly sustainEmpireTicks?: number;
}

export interface AodMapV1 {
  readonly schemaVersion: 'aod-map/1';
  readonly name: string;
  readonly description?: string;
  readonly author?: string;
  readonly tags?: readonly string[];
  readonly grid: {
    readonly projection: 'hex';
    readonly width: number;
    readonly height: number;
    readonly origin: 'odd-r';
  };
  readonly tiles: readonly AodMapTile[];
  readonly rivers?: readonly AodMapRiver[];
  readonly walls?: readonly AodMapWall[];
  readonly resources?: readonly AodMapResource[];
  readonly cities?: readonly AodMapCity[];
  readonly starts: readonly AodMapStart[];
  readonly places?: readonly AodMapPlace[];
  readonly victory?: AodMapVictory;
  /** `aod-map/1:<sha256[..12]>` — computed by the publisher, trusted here. */
  readonly contentHash?: string;
  /**
   * The ruleset the map was authored for — the author's default. Optional:
   * a map says *where*, the ruleset says *how*, and the match-server falls
   * back to the default ruleset when a map declares none.
   *
   * The Rust mirror accepts this field and ignores it (the caller supplies
   * the ruleset); the match-server is that caller, so it reads the field
   * and hands the ruleset to the wasm.
   */
  readonly ruleset?: Ruleset;
  /**
   * The seats the map was authored for — one per start, humans first.
   * Optional, with the same accepted-and-ignored deal as `ruleset`: the
   * match-server reads it, checks it against the match's roster, and
   * falls back to the roster's own seats when a map declares none.
   */
  readonly seats?: readonly Seat[];
}

/** The largest board the client can render; bigger maps are rejected at import. */
export const AOD_MAP_MAX_TILES = 3600;

const isInt = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n);

/**
 * Strict structural validation for an `aod-map` document: shape, vocabulary,
 * bounds, duplicate coordinates and start sanity. Cross-references (a place
 * on a water tile, a start off a city) are advisory in the editor and only
 * become strict on the publish path — a document cannot tell a mid-edit map
 * from a finished one.
 *
 * @returns a list of problems; empty means the map is structurally valid.
 */
export function validateAodMap(doc: unknown): string[] {
  const problems: string[] = [];
  if (typeof doc !== 'object' || doc === null) return ['the document is not an object'];
  const m = doc as Record<string, unknown>;
  if (m.schemaVersion !== AOD_MAP_SCHEMA_VERSION) {
    problems.push(`schemaVersion must be "${AOD_MAP_SCHEMA_VERSION}"`);
    return problems;
  }
  if (typeof m.name !== 'string' || m.name.trim().length === 0) {
    problems.push('name is required');
  }
  const grid = m.grid as AodMapV1['grid'] | undefined;
  if (
    typeof grid !== 'object' ||
    grid === null ||
    grid.projection !== 'hex' ||
    grid.origin !== 'odd-r' ||
    !isInt(grid.width) ||
    !isInt(grid.height) ||
    grid.width < 4 ||
    grid.height < 4
  ) {
    problems.push('grid must be { projection: "hex", width >= 4, height >= 4, origin: "odd-r" }');
    return problems;
  }
  const width = grid.width;
  const height = grid.height;
  if (!Array.isArray(m.tiles) || m.tiles.length === 0) {
    problems.push('tiles is required');
    return problems;
  }
  if (m.tiles.length > AOD_MAP_MAX_TILES) {
    problems.push(
      `the board exceeds the render limit (${m.tiles.length} > ${AOD_MAP_MAX_TILES} tiles)`,
    );
  }

  const seen = new Set<string>();
  const key = (q: number, r: number) => `${q},${r}`;
  const tileKeys = new Set<string>();
  for (const t of m.tiles as AodMapTile[]) {
    if (
      typeof t !== 'object' ||
      t === null ||
      !isInt(t.q) ||
      !isInt(t.r) ||
      !AOD_BIOMES.includes(t.biome)
    ) {
      problems.push('every tile needs integer q, r and one of the nine AoD biomes');
      continue;
    }
    if (t.q < 0 || t.q >= width || t.r < 0 || t.r >= height) {
      problems.push(`tile (${t.q},${t.r}) is outside the ${width}x${height} grid`);
      continue;
    }
    const k = key(t.q, t.r);
    if (seen.has(k)) problems.push(`duplicate tile at (${t.q},${t.r})`);
    seen.add(k);
    tileKeys.add(k);
    if (t.variant !== undefined && (!isInt(t.variant) || t.variant < 0 || t.variant > 255)) {
      problems.push(`tile (${t.q},${t.r}): variant must be 0..255`);
    }
    if (
      t.terrainLevel !== undefined &&
      (!isInt(t.terrainLevel) || t.terrainLevel < 0 || t.terrainLevel > 12)
    ) {
      problems.push(`tile (${t.q},${t.r}): terrainLevel must be 0..=12`);
    }
  }
  if (tileKeys.size < width * height) {
    problems.push(
      `the grid is not fully covered: ${tileKeys.size} of ${width * height} tiles present`,
    );
  }

  const coordOnLand = (q: number, r: number): boolean => {
    for (const t of m.tiles as AodMapTile[]) {
      if (t.q === q && t.r === r) return t.biome !== 'ocean';
    }
    return false;
  };

  if (m.rivers !== undefined) {
    for (const rv of m.rivers as AodMapRiver[]) {
      if (
        typeof rv !== 'object' ||
        rv === null ||
        !isInt(rv.q) ||
        !isInt(rv.r) ||
        !isInt(rv.edges) ||
        rv.edges < 0 ||
        rv.edges > 63 ||
        !isInt(rv.bridges) ||
        rv.bridges < 0 ||
        rv.bridges > 63
      ) {
        problems.push(`river at (${rv?.q},${rv?.r}): edges and bridges must be 6-bit masks`);
      }
    }
  }
  if (m.walls !== undefined && !Array.isArray(m.walls)) problems.push('walls must be a list');
  if (m.resources !== undefined) {
    for (const res of m.resources as AodMapResource[]) {
      if (
        typeof res !== 'object' ||
        res === null ||
        !isInt(res.q) ||
        !isInt(res.r) ||
        typeof res.kind !== 'string' ||
        res.kind.length === 0
      ) {
        problems.push('every resource needs integer q, r and a kind');
        continue;
      }
      if (!seen.has(key(res.q, res.r))) {
        problems.push(`resource at (${res.q},${res.r}) is not on a tile of this map`);
      }
    }
  }
  if (m.cities !== undefined) {
    for (const c of m.cities as AodMapCity[]) {
      if (
        typeof c !== 'object' ||
        c === null ||
        !isInt(c.q) ||
        !isInt(c.r) ||
        typeof c.name !== 'string' ||
        c.name.trim().length === 0 ||
        !isInt(c.tier)
      ) {
        problems.push('every city needs integer q, r, a name and an integer tier');
        continue;
      }
      if (!coordOnLand(c.q, c.r)) {
        problems.push(`city "${c.name}" at (${c.q},${c.r}) is not on land`);
      }
    }
  }
  if (!Array.isArray(m.starts) || m.starts.length === 0) {
    problems.push('starts is required (at least one player start)');
  } else {
    const startOwners = new Set<number>();
    for (const st of m.starts as AodMapStart[]) {
      if (
        typeof st !== 'object' ||
        st === null ||
        !isInt(st.playerIndex) ||
        !isInt(st.q) ||
        !isInt(st.r)
      ) {
        problems.push('every start needs an integer playerIndex, q and r');
        continue;
      }
      if (st.playerIndex < 0) problems.push(`start playerIndex ${st.playerIndex} is negative`);
      if (startOwners.has(st.playerIndex)) {
        problems.push(`player ${st.playerIndex} has more than one start`);
      }
      startOwners.add(st.playerIndex);
      if (!coordOnLand(st.q, st.r)) {
        problems.push(`player ${st.playerIndex} starts on water at (${st.q},${st.r})`);
      }
    }
  }
  if (m.places !== undefined) {
    for (const pl of m.places as AodMapPlace[]) {
      if (
        typeof pl !== 'object' ||
        pl === null ||
        typeof pl.id !== 'string' ||
        !/^[a-z][a-z0-9-]*$/.test(pl.id) ||
        typeof pl.name !== 'string' ||
        pl.name.trim().length === 0 ||
        !isInt(pl.q) ||
        !isInt(pl.r)
      ) {
        problems.push('every place needs a lowercase-slug id, a name and integer q, r');
        continue;
      }
      if (!seen.has(key(pl.q, pl.r))) {
        problems.push(`place "${pl.id}" is not on a tile of this map`);
      }
    }
  }
  if (m.victory !== undefined) {
    const v = m.victory as AodMapVictory;
    if (typeof v !== 'object' || v === null) {
      problems.push('victory must be an object');
    } else {
      if (v.paths !== undefined) {
        if (
          !Array.isArray(v.paths) ||
          v.paths.length === 0 ||
          !v.paths.every((p) => typeof p === 'string')
        ) {
          problems.push('victory.paths must be a non-empty list of strings');
        }
      }
      if (
        v.diplomacyHonor !== undefined &&
        (!isInt(v.diplomacyHonor) || v.diplomacyHonor < 0 || v.diplomacyHonor > 100)
      ) {
        problems.push('victory.diplomacyHonor must be 0..100');
      }
      if (v.minEmpireTick !== undefined && (!isInt(v.minEmpireTick) || v.minEmpireTick < 0)) {
        problems.push('victory.minEmpireTick must be >= 0');
      }
      if (
        v.sustainEmpireTicks !== undefined &&
        (!isInt(v.sustainEmpireTicks) || v.sustainEmpireTicks < 0)
      ) {
        problems.push('victory.sustainEmpireTicks must be >= 0');
      }
    }
  }
  return problems;
}
